import { track } from '../../analytics/analytics';
import { CLASSIC_SHOP_URL } from '../../app/constants';
import { getCartService, notifyCartChanged } from '../../commerce/cart.service';
import { CommerceError, type ShopProduct } from '../../commerce/shopify.types';
import { badgesFor, renderBadges } from '../product-badges/product-badges';
import { createProductCarousel, type ProductCarousel } from './product-carousel';
import {
  createBadge,
  createFaq,
  createProductOffer,
  createReassurance,
  createReviews,
  type ProductOffer,
} from './product-offer';

export interface ProductPanel {
  element: HTMLElement;
  open(product: ShopProduct): void;
  openError(message: string): void;
  close(): void;
  isOpen(): boolean;
}

function formatPrice(product: ShopProduct): string {
  const amount = Number.parseFloat(product.price.amount);
  if (Number.isNaN(amount)) return '';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: product.price.currencyCode || 'EUR',
  }).format(amount);
}

/**
 * Fiche légère : elle ne rejoue pas toute la page produit Shopify et laisse
 * le décor visible derrière. Panneau latéral sur desktop, bottom sheet sur mobile.
 */
export interface ProductPanelOptions {
  /** Ouvre le panneau panier. Sans lui, le bouton renvoie vers le checkout. */
  onOpenCart?: () => void;
  /** Prévenu à la fermeture, pour ranger le produit sorti de l'étagère. */
  onClose?: () => void;
}

export function createProductPanel(
  parent: HTMLElement,
  options: ProductPanelOptions = {},
): ProductPanel {
  const element = document.createElement('aside');
  element.className = 'product-panel';
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-modal', 'false');
  element.setAttribute('aria-label', 'Fiche produit');
  element.hidden = true;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'product-panel__close';
  close.setAttribute('aria-label', 'Fermer la fiche produit');
  close.textContent = '×';

  const body = document.createElement('div');
  body.className = 'product-panel__body';

  element.append(close, body);
  parent.append(element);

  let lastFocused: HTMLElement | null = null;
  let dernierDefilement = 0;
  /** Bloc d'achat de la fiche ouverte : c'est lui qui sait quoi mettre au panier. */
  let offre: ProductOffer | null = null;
  /** Carrousel de la fiche ouverte, à démonter avant d'en ouvrir une autre. */
  let carrousel: ProductCarousel | null = null;

  function hide(): void {
    element.hidden = true;
    element.classList.remove('is-open');
    lastFocused?.focus();
    lastFocused = null;
    options.onClose?.();
  }

  function show(): void {
    dernierDefilement = Date.now();
    lastFocused = document.activeElement as HTMLElement | null;
    element.hidden = false;
    requestAnimationFrame(() => element.classList.add('is-open'));
    close.focus();
  }

  function openError(message: string): void {
    body.replaceChildren();
    const paragraph = document.createElement('p');
    paragraph.className = 'product-panel__error';
    paragraph.textContent = message;

    const link = document.createElement('a');
    link.className = 'product-panel__action product-panel__action--ghost';
    link.href = CLASSIC_SHOP_URL;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    link.textContent = 'Ouvrir la boutique classique';

    body.append(paragraph, link);
    show();
  }

  function open(product: ShopProduct): void {
    body.replaceChildren();
    track('product_opened', { productHandle: product.handle });

    const title = document.createElement('h2');
    title.className = 'product-panel__title';
    title.textContent = product.title;

    const price = document.createElement('p');
    price.className = 'product-panel__price';
    price.textContent = formatPrice(product);

    const availability = document.createElement('p');
    availability.className = 'product-panel__availability';
    availability.dataset['available'] = String(product.availableForSale);
    availability.textContent = product.availableForSale
      ? 'Disponible'
      : 'Momentanément indisponible';

    // Le bloc d'achat complet : arguments, onglets, déclinaisons, abonnement.
    // Il porte son propre prix, à jour de la déclinaison choisie, donc le prix
    // d'en-tête ne sert plus qu'aux fiches sans choix possible.
    offre = createProductOffer(product);
    price.hidden = (product.variants?.length ?? 0) > 1;

    const actions = document.createElement('div');
    actions.className = 'product-panel__actions';

    const view = document.createElement('a');
    view.className = 'product-panel__action product-panel__action--ghost';
    view.href = product.productUrl ?? `${CLASSIC_SHOP_URL}/products/${product.handle}`;
    view.rel = 'noopener noreferrer';
    view.target = '_blank';
    view.textContent = 'Voir le produit';

    actions.append(view);

    if (product.availableForSale) {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'product-panel__action product-panel__action--primary';
      add.textContent = 'Ajouter au panier';
      add.addEventListener('click', () => {
        void addToCart(product, add);
      });
      actions.append(add);
    } else {
      // Un bouton désactivé explique pourquoi l'achat est impossible ; l'absence
      // de bouton, elle, ne disait rien. Il n'a aucun gestionnaire de clic.
      const unavailable = document.createElement('button');
      unavailable.type = 'button';
      unavailable.className = 'product-panel__action product-panel__action--unavailable';
      unavailable.disabled = true;
      unavailable.textContent = 'Momentanément indisponible';
      unavailable.setAttribute(
        'aria-label',
        `${product.title} est momentanément indisponible, ajout au panier impossible`,
      );
      actions.append(unavailable);
    }

    const feedback = document.createElement('div');
    feedback.className = 'product-panel__feedback';
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');

    /*
     * Assemblage repris de la fiche produit du site, dans le même ordre :
     * avis, titre, badge, repères, onglets et offre, ajout au panier, deux
     * lignes de réassurance, puis la fiche en clair.
     */
    // Les photos ouvrent la fiche, comme sur le site : sur un écran étroit
    // c'est le produit qu'on voit en premier, pas une ligne de texte.
    carrousel?.destroy();
    carrousel = createProductCarousel(product);
    if (carrousel) body.append(carrousel.element);

    const avis = createReviews(product);
    if (avis) body.append(avis);
    body.append(title);
    const badge = createBadge(product);
    if (badge) body.append(badge);
    body.append(price, availability);
    body.append(offre.element, actions);
    if (product.availableForSale) body.append(createReassurance());
    body.append(feedback);

    /*
     * Les repères produit viennent APRÈS la réassurance et avant la fiche en
     * clair : ce sont des caractéristiques, pas un argument d'achat, et les
     * placer entre le prix et la description repoussait l'offre hors de l'écran.
     *
     * La note est exclue : elle est déjà en tête de fiche.
     */
    const reperes = badgesFor(product.details).filter((repere) => repere.ton !== 'avis');
    if (reperes.length) body.append(renderBadges(reperes));

    const fiche = createFaq(product);
    if (fiche) body.append(fiche);
    show();
  }

  async function addToCart(product: ShopProduct, button: HTMLButtonElement): Promise<void> {
    const feedback = body.querySelector<HTMLElement>('.product-panel__feedback');
    // Dernier verrou : un produit indisponible ne rejoint jamais le panier.
    if (!product.availableForSale) {
      if (feedback) feedback.textContent = 'Ce produit est momentanément indisponible.';
      return;
    }
    // Ce que le visiteur a choisi dans le bloc d'achat : déclinaison, et
    // abonnement s'il l'a coché. Sans bloc, on retombe sur le produit nu.
    const choix = offre?.selection();
    const variante = choix?.variant;
    if (variante && !variante.availableForSale) {
      if (feedback) feedback.textContent = 'Cette déclinaison est momentanément indisponible.';
      return;
    }

    button.disabled = true;
    button.dataset['loading'] = 'true';
    button.textContent = 'Ajout en cours…';

    try {
      const cart = getCartService();
      // Un lot commande plusieurs exemplaires de la même déclinaison : c'est
      // ainsi que Moon Bundles procède, la remise étant appliquée au panier.
      const quantite = choix?.quantite && choix.quantite > 0 ? choix.quantite : 1;
      const nom = choix?.lot
        ? `${product.title} · ${choix.lot}`
        : variante && variante.title !== product.title
          ? `${product.title} · ${variante.title}`
          : product.title;

      await cart.addLine(variante?.id ?? product.variantId, quantite, {
        title: nom,
        price: variante?.price ?? product.price,
        imageUrl: product.imageUrl,
        imageAlt: product.imageAlt,
        ...(choix?.sellingPlan ? { sellingPlanId: choix.sellingPlan.id } : {}),
      });
      notifyCartChanged();
      track('product_added_to_cart', { productHandle: product.handle });

      button.textContent = 'Ajouté au panier';
      delete button.dataset['loading'];

      if (feedback) {
        feedback.replaceChildren();
        const message = document.createElement('p');
        message.textContent = `${product.title} est dans le panier. Le chien fait semblant de ne pas regarder.`;

        const keep = document.createElement('button');
        keep.type = 'button';
        keep.className = 'product-panel__action product-panel__action--ghost';
        keep.textContent = 'Continuer la visite';
        keep.addEventListener('click', hide);

        // Le panier s'ouvre dans la boutique : on ne quitte la visite qu'au paiement.
        let checkout: HTMLElement;
        if (options.onOpenCart) {
          const open = document.createElement('button');
          open.type = 'button';
          open.className = 'product-panel__action product-panel__action--primary';
          open.textContent = 'Voir mon panier';
          open.addEventListener('click', () => options.onOpenCart?.());
          checkout = open;
        } else {
          const link = document.createElement('a');
          link.className = 'product-panel__action product-panel__action--primary';
          link.rel = 'noopener noreferrer';
          link.target = '_blank';
          link.textContent = 'Voir mon panier';
          link.href = await cart.getCheckoutUrl();
          link.addEventListener('click', () => {
            track('checkout_opened', { productHandle: product.handle });
          });
          checkout = link;
        }

        const row = document.createElement('div');
        row.className = 'product-panel__actions';
        row.append(keep, checkout);
        feedback.append(message, row);
        // La confirmation ne doit pas rester sous la ligne de flottaison du panneau.
        // Un défilement indisponible ne doit jamais faire échouer l'ajout.
        try {
          feedback.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch {
          /* environnement sans scrollIntoView */
        }
      }
    } catch (error) {
      button.disabled = false;
      delete button.dataset['loading'];
      button.textContent = 'Ajouter au panier';
      const message =
        error instanceof CommerceError
          ? error.message
          : 'L’ajout au panier a échoué. Le produit reste disponible dans la boutique classique.';
      if (feedback) feedback.textContent = message;
    }
  }

  close.addEventListener('click', hide);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !element.hidden) hide();
  });

  /*
   * La fiche se referme dès qu'on retourne à la visite : un clic ailleurs, ou
   * le moindre déplacement dans le décor. Le clic est écouté à la phase de
   * capture pour ne pas dépendre de ce que fait la scène en dessous.
   */
  const onClicAilleurs = (event: PointerEvent): void => {
    if (element.hidden) return;
    const cible = event.target as HTMLElement | null;
    if (!cible) return;
    // Le plateau du produit sorti de l'étagère fait partie de la fiche.
    if (element.contains(cible) || cible.closest('.product-stage')) return;
    hide();
  };
  document.addEventListener('pointerdown', onClicAilleurs, true);

  /**
   * Défiler le décor referme la fiche, c'est le geste de reprise de visite.
   * Défiler DANS la fiche, en revanche, doit la laisser ouverte : depuis
   * qu'elle porte tout le bloc d'achat, elle est plus haute que l'écran et se
   * refermait au premier geste de lecture.
   */
  const dansLaFiche = (cible: EventTarget | null): boolean => {
    if (cible === element) return true;
    return cible instanceof Node && element.contains(cible);
  };

  const onDefilement = (event: Event): void => {
    if (element.hidden) return;
    if (dansLaFiche(event.target)) return;
    // Une inertie de défilement peut arriver juste après l'ouverture : on
    // laisse passer un court instant avant de refermer.
    if (Date.now() - dernierDefilement < 350) return;
    hide();
  };
  document.addEventListener('scroll', onDefilement, { capture: true, passive: true });
  window.addEventListener('wheel', onDefilement, { passive: true });

  return {
    element,
    open,
    openError,
    close: hide,
    isOpen: () => !element.hidden,
  };
}
